import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import useSponsorProfileSeo from "@/hooks/useSponsorProfileSeo";
import { HEYCATCH_EVENTS, trackOutcomeOnce } from "@/lib/heycatch";
import SponsorProfileHeader from "@/components/sponsors/SponsorProfileHeader";
import SponsorAvailabilityCard from "@/components/sponsors/SponsorAvailabilityCard";
import SponsorActionRail from "@/components/sponsors/SponsorActionRail";
import RequestSponsorshipDialog from "@/components/sponsors/RequestSponsorshipDialog";
import { Spinner } from "@/components/common/Loading";
import { ConfirmDialog, ReportDialog } from "@/components/safety/Dialogs";
import { toast } from "sonner";

export default function SponsorProfile() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user, openPaywall } = useAuth();
  const [sponsor, setSponsor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const [requestOpen, setRequestOpen] = useState(false);
  const [note, setNote] = useState("");
  const [reportOpen, setReportOpen] = useState(false);
  const [blockOpen, setBlockOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/sponsors/${userId}`);
      setSponsor(data);
    } catch (e) {
      toast.error(apiError(e, "This sponsor profile is not available."));
      navigate("/sponsors");
    } finally {
      setLoading(false);
    }
  }, [userId, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  useSponsorProfileSeo(sponsor, userId);

  const requireAuth = useCallback(() => {
    if (!user) {
      navigate(`/register?next=/sponsors/${userId}`);
      return false;
    }
    return true;
  }, [user, navigate, userId]);

  const sendRequest = useCallback(async () => {
    if (!requireAuth() || !sponsor) return;
    setRequesting(true);
    try {
      await api.post("/requests", { sponsor_user_id: sponsor.user_id, message: note || null });
      toast.success(`Your sponsorship request was sent to ${sponsor.user.display_name}.`);
      // HeyCatch: an engagement outcome autocapture cannot see. No sponsor id,
      // recovery program, or request note is forwarded - the event carries no
      // properties at all. Guarded per member + sponsor pair so a retry after a
      // transient failure cannot double-count it.
      trackOutcomeOnce(
        `request:${user?.id}:${sponsor.user_id}`,
        HEYCATCH_EVENTS.SPONSORSHIP_REQUESTED
      );
      setRequestOpen(false);
      setNote("");
      load();
    } catch (e) {
      if (e?.response?.status === 402) {
        openPaywall(e.response.data.detail);
      } else {
        toast.error(apiError(e));
      }
    } finally {
      setRequesting(false);
    }
  }, [requireAuth, sponsor, note, load, openPaywall, user?.id]);

  const startMessage = useCallback(async () => {
    if (!requireAuth() || !sponsor) return;
    try {
      const { data } = await api.post("/conversations", { other_user_id: sponsor.user_id });
      navigate(`/messages?c=${data.id}`);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, [requireAuth, sponsor, navigate]);

  const toggleFavorite = useCallback(async () => {
    if (!requireAuth() || !sponsor) return;
    try {
      if (sponsor.is_favorite) await api.delete(`/favorites/${sponsor.user_id}`);
      else await api.post(`/favorites/${sponsor.user_id}`);
      setSponsor((prev) => (prev ? { ...prev, is_favorite: !prev.is_favorite } : prev));
    } catch (e) {
      toast.error(apiError(e));
    }
  }, [requireAuth, sponsor]);

  if (loading) return <Spinner label="Loading profile" />;
  if (!sponsor) return null;

  const u = sponsor.user;
  const isSelf = user?.id === sponsor.user_id;
  const loc = [u.location?.city, u.location?.state, u.location?.country].filter(Boolean).join(", ");

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <Link
        to="/sponsors"
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1F6F78]"
      >
        <ArrowLeft className="w-4 h-4" /> Back to directory
      </Link>

      <div className="mt-4 grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          <SponsorProfileHeader sponsor={sponsor} user={u} loc={loc} />

          <div className="sm-card p-6 bg-white">
            <h2 className="font-display text-xl font-bold text-[#1F2A2E]">About Me</h2>
            <p className="mt-3 text-[#1F2A2E] leading-7 whitespace-pre-line">{sponsor.about_me}</p>
          </div>

          {sponsor.sponsorship_style ? (
            <div className="sm-card p-6 bg-white">
              <h2 className="font-display text-xl font-bold text-[#1F2A2E]">My Approach</h2>
              <p className="mt-3 text-[#1F2A2E] leading-7 whitespace-pre-line">
                {sponsor.sponsorship_style}
              </p>
            </div>
          ) : null}

          <SponsorAvailabilityCard sponsor={sponsor} />
        </div>

        {/* Action rail */}
        <SponsorActionRail
          sponsor={sponsor}
          isSelf={isSelf}
          navigate={navigate}
          requireAuth={requireAuth}
          onOpenRequest={() => setRequestOpen(true)}
          onStartMessage={startMessage}
          onToggleFavorite={toggleFavorite}
          onOpenReport={() => setReportOpen(true)}
          onOpenBlock={() => setBlockOpen(true)}
        />
      </div>

      {/* Request dialog */}
      <RequestSponsorshipDialog
        open={requestOpen}
        displayName={u.display_name}
        note={note}
        setNote={setNote}
        requesting={requesting}
        onClose={() => setRequestOpen(false)}
        onSubmit={sendRequest}
      />

      <ReportDialog open={reportOpen} reportedUser={u} onClose={() => setReportOpen(false)} />
      <ConfirmDialog
        open={blockOpen}
        danger
        title={`Block ${u.display_name}?`}
        description="They will no longer be able to message you or see your profile, and any connection between you will end. You can unblock them later from your settings."
        confirmLabel="Block member"
        onClose={() => setBlockOpen(false)}
        onConfirm={async () => {
          try {
            await api.post(`/blocks/${sponsor.user_id}`);
            toast.success("Member blocked.");
            setBlockOpen(false);
            navigate("/sponsors");
          } catch (e) {
            toast.error(apiError(e));
          }
        }}
      />
    </div>
  );
}
